import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import Divider from '@material-ui/core/Divider';
import Typography from '@material-ui/core/Typography';
import QuantitySelector from '@components/QuantitySelector';
import AdditionalOptions from '@components/AdditionalOptions';
import { withStyles } from '@material-ui/core/styles';
import _get from 'lodash/get';
import _remove from 'lodash/remove';
import dayjs from 'dayjs';
import _cloneDeep from 'lodash/cloneDeep';
import _findIndex from 'lodash/findIndex';

var isToday = require('dayjs/plugin/isToday');
dayjs.extend(isToday);
import { useTranslation } from 'next-i18next';
import BoxModal from '@components/BoxModal';
import ConfigureBundleProductForm from '@components/ConfigureBundleProductForm';

const styles = theme => ({
  thHead: {
    color: '#808EAA',
    fontSize: 14,
  },
  td: {
    color: '#31415E',
  },
  removeIcon: {
    cursor: 'pointer',
  },
  variant: {
    marginBottom: 0,
  },
  itemContent: {
    width: '100%',
  },
  itemContentRowWrapper: {
    display: 'flex',
  },
  itemImageWrapper: {
    marginRight: 13,
    minWidth: 67,
    [theme.breakpoints.up('sm')]: {
      marginRight: 16,
    },
  },
  mobileBottomContainer: {
    marginTop: 16,
    flexFlow: 'nowrap',
  },
  topContent: {},
  itemImage: {
    borderRadius: 6,
  },
  addedDate: {
    color: '#FF7C1E',
  },
  addedDateWrapper: {
    paddingTop: 8,
    display: 'flex',
    alignItems: 'center',
  },
  notAllowHoldShipping: {
    color: '#808EAA',
  },
  notAllowHoldShippingWrapper: {
    paddingTop: 30,
    display: 'flex',
    alignItems: 'center',
    [theme.breakpoints.up('sm')]: {
      paddingTop: 15,
    },
  },
  addedDateIcon: {
    paddingRight: 5,
    position: 'relative',
    top: 2,
  },
  remarks: {
    color: '#808EAA',
  },
  outOfStock: {
    color: '#ff4d4f',
  },
  primaryColor: {
    color: '#076BD1',
  },
  itemRemarks: {
    color: '#808EAA',
  },
  nameWrapper: {
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%',
  },
  quantitySelector: {
    position: 'relative',
    top: 6,
  },
  subTotalWrapper: {
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%',
    alignItems: 'center',
  },
  subTotal: {
    fontSize: 20,
  },
  buttonRoot: {
    width: '100%',
  },
  bundleDealEditActionContainer: {
    backgroundColor: '#FEFAEB',
    borderRadius: 10,
    padding: '10px 12px',
    display: 'flex',
    justifyContent: 'space-between',
    color: '#076BD1',
    marginTop: 10,
    cursor: 'pointer',
  },
  bundleDealLabel: {
    color: '#076BD1',
    borderRadius: 10,
    border: '1px solid #076BD1',
    padding: '4.5px 9px',
    fontSize: 12,
    lineHeight: '12px',
    fontFamily: [
      '"CircularStd-Medium"',
      'sans-serif',
      '"Apple Color Emoji"',
      '"Segoe UI Emoji"',
      '"Segoe UI Symbol"',
    ].join(','),
  },
  bundleItemWrapper: {
    backgroundColor: '#F6F7F9',
    borderRadius: 10,
    padding: '10px 11px',
    marginTop: 8,
  },
  mobileItemLabel: {
    color: '#808EAA',
    width: 60,
    textAlign: 'left',
    flex: 1,
    paddingLeft: 30,
  },
  bundleItemName: {
    flex: '1 0 200px',
  },
});

const ExpressCart = ({
  classes,
  isAllowToChangeQuantity,
  isAllowToChangeWebStoreQuantity,
  data = [],
  onChangeCartItem = null,
  loading,
  subTotal,
  onClickPlaceOrder = () => {},
  currency = 'MYR',
}) => {
  const { t } = useTranslation('common');
  const [cartData, setCartData] = useState(data);
  const [selectedOrderRow, setSelectedOrderRow] = useState(null);

  useEffect(() => {
    setCartData(data);
  }, [data]);

  const removeItem = id => {
    setCartData(prevCartData => {
      const finalCartData = _remove(prevCartData, prevData => prevData.id !== id);
      if (typeof onChangeCartItem === 'function') {
        onChangeCartItem(finalCartData);
      }
      return finalCartData;
    });
  };

  const updateQty = (id, quantity) => {
    setCartData(prevCartData => {
      const finalCartData = prevCartData.map(prevData => {
        if (prevData.id === id) {
          return {
            ...prevData,
            quantity,
          };
        } else {
          return prevData;
        }
      });
      if (typeof onChangeCartItem === 'function') {
        onChangeCartItem(finalCartData);
      }
      return finalCartData;
    });
  };

  const onSelectCustomOption = (id, options) => {
    setCartData(prevCartData => {
      const finalCartData = prevCartData.map(prevData => {
        if (prevData.id === id) {
          return {
            ...prevData,
            options,
          };
        } else {
          return prevData;
        }
      });
      if (typeof onChangeCartItem === 'function') {
        onChangeCartItem(finalCartData);
      }
      return finalCartData;
    });
  };

  const onClickEditBundleSubItems = e => {
    setSelectedOrderRow(e);
  };

  const onSubmitConfigureBundleProductForm = selectedSubProducts => {
    if (_get(selectedOrderRow, 'id')) {
      setCartData(prevCartData => {
        const newCartData = _cloneDeep(prevCartData);
        const foundIndex = _findIndex(newCartData, {
          id: _get(selectedOrderRow, 'id'),
        });
        if (foundIndex > -1) {
          newCartData[foundIndex].sub_items = selectedSubProducts;
        }
        if (typeof onChangeCartItem === 'function') {
          onChangeCartItem(newCartData);
        }
        return newCartData;
      });
      setSelectedOrderRow(null);
    }
  };

  return (
    <>
      <div>
        {cartData.length > 0 ? (
          <>
            <div className={classes.orderItemsWrapper}>
              {cartData.map(row => {
                const image = _get(row, 'cover_image.url', '/static/images/placeholderImage.png');
                let allowToEdit =
                  isAllowToChangeQuantity && _get(row, 'product_info.product_type') !== 'bundle';
                if (_get(row, 'is_from_webstore', false)) {
                  allowToEdit =
                    isAllowToChangeWebStoreQuantity &&
                    _get(row, 'product_info.product_type') !== 'bundle';
                }
                return (
                  <div key={row.id}>
                    <div className={classes.itemContentRowWrapper}>
                      <div className={classes.itemImageWrapper}>
                        <img src={image} width="67" className={classes.itemImage} />
                      </div>
                      <div className={classes.itemContent}>
                        <div className={classes.topContent}>
                          <div className={classes.nameWrapper}>
                            <span className={classes.td}>{row.name}</span>
                            {allowToEdit ? (
                              <div>
                                <img
                                  src="/static/images/remove.svg"
                                  className={classes.removeIcon}
                                  title="Remove this item"
                                  onClick={removeItem.bind(this, row.id)}
                                />
                              </div>
                            ) : null}
                          </div>
                          {row.remarks ? (
                            <Typography className={classes.itemRemarks}>
                              {row.remarks.split('\n').map(function(item, idx) {
                                return (
                                  <span key={idx}>
                                    {item}
                                    <br />
                                  </span>
                                );
                              })}
                            </Typography>
                          ) : (
                            ''
                          )}
                          {row.created_at && dayjs(row.created_at).isToday() === false && (
                            <div className={classes.addedDateWrapper}>
                              <div className={classes.addedDateIcon}>
                                <img src="/static/images/clock.svg" width="14px" />
                              </div>
                              <small className={classes.addedDate}>
                                {t('added-on')} {dayjs(row.created_at).format('MMMM D, YYYY')}
                              </small>
                            </div>
                          )}
                        </div>
                        <Grid
                          container
                          alignItems="center"
                          justify="space-between"
                          className={classes.mobileBottomContainer}
                        >
                          <Grid item>
                            <span className={classes.td}>
                              {row.price ? `${currency} ${row.price.toFixed(2)}` : '-'}
                            </span>
                          </Grid>
                          <Grid item>
                            <div className={allowToEdit ? classes.quantitySelector : ''}>
                              <QuantitySelector
                                isAllowToChangeQuantity={allowToEdit}
                                value={row.quantity}
                                onQtyChanged={updateQty.bind(this, row.id)}
                                loading={loading}
                                limit_qty_per_customer={row.limit_qty_per_customer}
                                min_qty_per_customer={row.min_qty_per_customer}
                              />
                            </div>
                          </Grid>
                        </Grid>
                        <br />
                        {row.discount_value ? (
                          <Grid container alignItems="center" justify="flex-end">
                            <Grid item className={classes.mobileItemLabel}>
                              {t('discount')}
                            </Grid>
                            <Grid item className={classes.mobileItemColumn}>
                              {row.discount_value
                                ? `- ${currency} ${row.discount_value.toFixed(2)}`
                                : '-'}
                            </Grid>
                          </Grid>
                        ) : null}
                        <Grid container alignItems="center" justify="flex-end">
                          <Grid item className={classes.mobileItemLabel}>
                            {t('total')}
                          </Grid>
                          <Grid item className={classes.mobileItemColumn}>
                            <span className={classes.primaryColor}>
                              {row.row_total ? `${currency} ${row.row_total.toFixed(2)}` : '-'}
                            </span>
                          </Grid>
                        </Grid>
                      </div>
                    </div>
                    <>
                      {_get(row, 'product_info.product_type') === 'group' ? (
                        <div className={classes.bundleDealEditActionContainer}>
                          <div className={classes.bundleDealLabel}>{t('group-deal')}</div>
                        </div>
                      ) : null}
                      {_get(row, 'product_info.product_type') === 'bundle' ? (
                        <div
                          className={classes.bundleDealEditActionContainer}
                          onClick={() => onClickEditBundleSubItems(row)}
                        >
                          <div className={classes.bundleDealLabel}>{t('bundle-deal')}</div>
                          <div>
                            <u>{t('choose-bundle-items')}</u>
                          </div>
                        </div>
                      ) : null}
                      {_get(row, 'sub_items', []).length > 0
                        ? _get(row, 'sub_items', []).map((data, i) => {
                            const name = _get(data, 'product.has_variation')
                              ? `${_get(data, 'product.name')} / ${_get(data, 'name')}`
                              : _get(data, 'name');
                            return (
                              <Grid
                                container
                                key={i}
                                className={classes.bundleItemWrapper}
                                justifyContent="space-between"
                              >
                                <Grid item className={classes.bundleItemName}>
                                  {name}
                                </Grid>
                                <Grid item>
                                  &nbsp;&nbsp;x{' '}
                                  {_get(data, 'quantity') || _get(data, 'quantity_in_group')}
                                </Grid>
                              </Grid>
                            );
                          })
                        : ''}
                      <Divider />
                      <AdditionalOptions
                        orderItemId={row.id}
                        custom_options={_get(row, 'product_info.custom_options', [])}
                        selected_options={row?.options}
                        onSelectCustomOption={options => onSelectCustomOption(row.id, options)}
                      />
                    </>
                    <Divider />
                  </div>
                );
              })}
            </div>
            <div className={classes.subTotalWrapper}>
              <div>{t('subtotal')}</div>
              <div className={classes.subTotal}>
                {currency} {subTotal.toFixed(2)}
              </div>
            </div>
            <br />
            <div>
              <Button
                color="primary"
                variant="contained"
                size="large"
                classes={{
                  root: classes.buttonRoot,
                  label: classes.buttonLabel,
                }}
                onClick={() => onClickPlaceOrder()}
              >
                <div>{t('place-your-order')}</div>
              </Button>
            </div>
          </>
        ) : (
          <div>{t('no-items-added')}</div>
        )}
      </div>
      <BoxModal
        open={selectedOrderRow}
        heading={t('choose-bundle-items')}
        handleClose={() => setSelectedOrderRow(null)}
      >
        <ConfigureBundleProductForm
          product_detail_id={_get(selectedOrderRow, 'product_detail_id')}
          subItems={_get(selectedOrderRow, 'sub_items', [])}
          onSubmit={selectedSubProducts => {
            onSubmitConfigureBundleProductForm(selectedSubProducts);
          }}
          onCancel={() => {
            setSelectedOrderRow(null);
          }}
          bundle_criteria={_get(selectedOrderRow, 'product_info.bundle_criteria', [])}
          bundle_discount_type={_get(selectedOrderRow, 'product_info.bundle_discount_type')}
          bundleMin={_get(selectedOrderRow, 'product_info.bundle_min', 0)}
          bundleMax={_get(selectedOrderRow, 'product_info.bundle_max', 0)}
        />
      </BoxModal>
    </>
  );
};

ExpressCart.propTypes = {
  classes: PropTypes.object.isRequired,
  isAllowToChangeQuantity: PropTypes.bool,
  isAllowToChangeWebStoreQuantity: PropTypes.bool,
  onChangeCartItem: PropTypes.func.isRequired,
  data: PropTypes.array,
  subTotal: PropTypes.number,
  loading: PropTypes.bool,
  onClickPlaceOrder: PropTypes.func,
  currency: PropTypes.string,
};
export default withStyles(styles)(ExpressCart);
